<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MailAccountConfig',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const recipient = ref(null);
  const sendMailTestResult = ref(null);
  const formId = ref(null);
  const sendTestEmailFormId = ref(null);
  const formName = ref('MailAccountConfig');
  const sendTestEmailFormName = ref('SendTestEmail');
  const config = ref({
    tls: {
      label: $fn.value.pgettext('MailAccount', 'Use TLS'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Enable TLS encryption for the connection.'
      ),
      default: true,
    },
    ssl: {
      label: $fn.value.pgettext('MailAccount', 'SSL'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Use SSL to secure the connection.'
      ),
      default: true,
    },
    use_credentials: {
      label: $fn.value.pgettext('MailAccount', 'Use Credentials'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Require username and password to authenticate.'
      ),
      default: true,
    },
    validate_certs: {
      label: $fn.value.pgettext('MailAccount', 'Validate Certificates'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Check that server certificates are valid.'
      ),
      default: true,
    },
    mail_from_name: {
      label: $fn.value.pgettext('MailAccount', 'Mail From Name'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Name displayed as the sender of the email.'
      ),
    },
    server: {
      label: $fn.value.pgettext('MailAccount', 'Server'),
      help: $fn.value.pgettext('MailAccount', 'Address of the mail server.'),
      default: 'smtp.server.com',
    },
    port: {
      label: $fn.value.pgettext('MailAccount', 'Port'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Port number for the mail server connection.'
      ),
      default: 587,
    },
    debug: {
      label: $fn.value.pgettext('MailAccount', 'Debug'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Show detailed connection and sending logs.'
      ),
      default: false,
    },
    username: {
      label: $fn.value.pgettext('MailAccount', 'Username'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Your login name for authentication.'
      ),
    },
    password: {
      label: $fn.value.pgettext('MailAccount', 'Password'),
      help: $fn.value.pgettext('MailAccount', 'Your account password.'),
    },
    confirm_password: {
      label: $fn.value.pgettext('MailAccount', 'Confirm Password'),
      help: $fn.value.pgettext('MailAccount', 'Re-enter password to verify.'),
    },
    timeout: {
      label: $fn.value.pgettext('MailAccount', 'Timeout'),
      help: $fn.value.pgettext('MailAccount', 'Time to wait before giving up.'),
      default: 60,
    },
    description: {
      label: $fn.value.pgettext('MailAccount', 'Description'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Short note or label for this entry.'
      ),
    },
    address: {
      label: $fn.value.pgettext('MailAccount', 'Address'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Email or server address to use.'
      ),
      default: 'username@provider.com',
    },
  });
  const resource = ref(null);
  const editor = ref(null);
  const change_password = ref({
    password: null,
    confirm_password: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    recipient: recipient.value,
    sendMailTestResult: sendMailTestResult.value,
    formId: formId.value,
    sendTestEmailFormId: sendTestEmailFormId.value,
    formName: formName.value,
    sendTestEmailFormName: sendTestEmailFormName.value,
    config: config.value,
    resource: resource.value,
    editor: editor.value,
    change_password: change_password.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_recipient = () => {
    return $app.value.auth.user.email;
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_sendTestEmailFormId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = () => {
    return props.oid
      ? $fn.value.fetch('get_object_by_type', {
          parameters: { id: props.oid, object_type: 'mail-account' },
          asResource: true,
        })
      : new $fn.value.Resource(
          _.reduce($data.value.config, (m, v, k) =>
            _.set(m, k, _.get(v, 'default', null))
          )
        );
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'recipient' },
      { name: 'sendMailTestResult' },
      { name: 'formId' },
      { name: 'sendTestEmailFormId' },
      { name: 'formName' },
      { name: 'sendTestEmailFormName' },
      { name: 'config' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'change_password' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      recipient: $$load_recipient,
      formId: $$load_formId,
      sendTestEmailFormId: $$load_sendTestEmailFormId,
      resource: $$load_resource,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      recipient,
      sendMailTestResult,
      formId,
      sendTestEmailFormId,
      formName,
      sendTestEmailFormName,
      config,
      resource,
      editor,
      change_password,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext(
            'MailAccountConfig|Title',
            'Mail account configuration'
          ),
        ],
        icon: $config.value.settings.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_iae324sqr_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_jqendr0q5_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_mf7654fda_getActionArgs = async ($event) => {
    return {
      objectType: 'object_by_type',
      op: 'upsert',
      data: {
        ...editor.value.getData(),
        object_type: 'mail-account',
        ...(change_password.value.password &&
        change_password.value.confirm_password
          ? change_password.value
          : {}),
      },
    };
  };
  const act_4186377094414664_closeModal_z7d9nn2m8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_4186377094414664_crud_tvoxax3xy_getActionArgs = async ($event) => {
    return {
      objectType: 'object_by_type',
      op: 'upsert',
      data: {
        ...editor.value.getData(),
        object_type: 'mail-account',
        ...(change_password.value.password &&
        change_password.value.confirm_password
          ? change_password.value
          : {}),
      },
    };
  };
  const act_2928017519152566_setData_4r8t43e72_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'sendMailTestResult',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2928017519152566_script_lrqftyw0h_value_callable = async (
    $event
  ) => {
    return (sendMailTestResult.value = 'success');
  };
  const act_2928017519152566_script_lrqftyw0h_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2928017519152566_script_lrqftyw0h_value_callable($event);
      },
    };
  };
  const act_2928017519152566_request_kwnf96u3h_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'post_sendmail_test',
      data: { mail_account: resource.value.id, recipient: recipient.value },
    };
  };
  const act_2140198529698299_closeModal_ro1esqduy_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_iju8ngtwu_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_object_by_type',
      data: resource.value.getData({ fields: ['id', 'etag'] }),
    };
  };
  const act_2167826738365259_closeModal_cyjj9q8yk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_iae324sqr =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_iae324sqr_getActionArgs,
    });
  const inst_evh_7315092382398562_close_u1xwhycf8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_iae324sqr,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_u1xwhycf8],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_11ywm2y5f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1eux64bz4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_11ywm2y5f,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1eux64bz4],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ag9tau4jd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_8c9i5as0a =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ag9tau4jd,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_8c9i5as0a],
    event: 'close',
  });
  const act_1040167445267876_closeModal_jqendr0q5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_jqendr0q5_getActionArgs,
    });
  const inst_evh_1040167445267876_success_vhglrbb3g =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_jqendr0q5,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_mf7654fda = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_mf7654fda_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_vhglrbb3g],
  });
  const inst_evh_1040167445267876_submit_zdx8mz8q3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_mf7654fda,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_zdx8mz8q3],
    event: 'submit',
  });
  const act_4186377094414664_closeModal_z7d9nn2m8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_4186377094414664_closeModal_z7d9nn2m8_getActionArgs,
    });
  const inst_evh_4186377094414664_success_n4khai2h9 =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeModal_z7d9nn2m8,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_4186377094414664_crud_tvoxax3xy = new actionTypes.CRUDAction({
    actionArgs: act_4186377094414664_crud_tvoxax3xy_getActionArgs,
    displayName: 'save',
    events: [inst_evh_4186377094414664_success_n4khai2h9],
  });
  const inst_evh_4186377094414664_submit_y4rlnd47h =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_crud_tvoxax3xy,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_y4rlnd47h],
    event: 'submit',
  });
  const act_2928017519152566_setData_4r8t43e72 = new actionTypes.SetDataAction({
    actionArgs: act_2928017519152566_setData_4r8t43e72_getActionArgs,
  });
  const inst_evh_2928017519152566_clickPreventStop_utl9f9iak =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_setData_4r8t43e72,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_2928017519152566_script_lrqftyw0h = new actionTypes.ScriptAction({
    actionArgs: act_2928017519152566_script_lrqftyw0h_getActionArgs,
  });
  const inst_evh_2928017519152566_success_6k5uoleip =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_script_lrqftyw0h,
      event: 'success',
      displayName: 'script',
    });
  const act_2928017519152566_request_kwnf96u3h = new actionTypes.RequestAction({
    actionArgs: act_2928017519152566_request_kwnf96u3h_getActionArgs,
    displayName: 'sendTestEmail',
    events: [inst_evh_2928017519152566_success_6k5uoleip],
  });
  const inst_evh_2928017519152566_clickPreventStop_ukp158gi2 =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_request_kwnf96u3h,
      event: 'click.prevent.stop',
      displayName: 'sendTestEmail',
    });
  const inst_evh_2928017519152566_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_2928017519152566_clickPreventStop_utl9f9iak,
        inst_evh_2928017519152566_clickPreventStop_ukp158gi2,
      ],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeModal_ro1esqduy =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_ro1esqduy_getActionArgs,
    });
  const inst_evh_2140198529698299_success_1s8995475 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_ro1esqduy,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_iju8ngtwu = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_iju8ngtwu_getActionArgs,
    displayName: 'delete',
    events: [inst_evh_2140198529698299_success_1s8995475],
  });
  const inst_evh_2140198529698299_clickPreventStop_cfb183sbo =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_iju8ngtwu,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_cfb183sbo],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_cyjj9q8yk =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_cyjj9q8yk_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_34t69sc5m =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_cyjj9q8yk,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_34t69sc5m],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_u1xwhycf8,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_1eux64bz4,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_8c9i5as0a,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_vhglrbb3g,
    inst_evh_1040167445267876_submit_zdx8mz8q3,
    save: inst_evh_1040167445267876_submit_zdx8mz8q3,
    inst_evh_1040167445267876_submit,
    inst_evh_4186377094414664_success_n4khai2h9,
    inst_evh_4186377094414664_submit_y4rlnd47h,
    inst_evh_4186377094414664_submit,
    inst_evh_2928017519152566_clickPreventStop_utl9f9iak,
    inst_evh_2928017519152566_success_6k5uoleip,
    inst_evh_2928017519152566_clickPreventStop_ukp158gi2,
    sendTestEmail: inst_evh_2928017519152566_clickPreventStop_ukp158gi2,
    inst_evh_2928017519152566_clickPreventStop,
    inst_evh_2140198529698299_success_1s8995475,
    inst_evh_2140198529698299_clickPreventStop_cfb183sbo,
    delete: inst_evh_2140198529698299_clickPreventStop_cfb183sbo,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_34t69sc5m,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_u1xwhycf8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_u1xwhycf8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_1eux64bz4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1eux64bz4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_8c9i5as0a = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_8c9i5as0a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_vhglrbb3g = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_vhglrbb3g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_zdx8mz8q3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_zdx8mz8q3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_success_n4khai2h9 = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_success_n4khai2h9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_y4rlnd47h = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_y4rlnd47h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop_utl9f9iak = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop_utl9f9iak.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_success_6k5uoleip = async (event, scope) => {
    return await actions.inst_evh_2928017519152566_success_6k5uoleip.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop_ukp158gi2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop_ukp158gi2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_1s8995475 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_1s8995475.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_cfb183sbo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_cfb183sbo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_34t69sc5m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_34t69sc5m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MailAccountConfig"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="address"
              :label="config.address.label"
              :helpTooltip="config.address.help"
              required
            >
              <ItpFormInput name="address" v-model="editor.address" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="description"
              :label="config.description.label"
              :helpTooltip="config.description.help"
            >
              <ItpFormInput name="description" v-model="editor.description">
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="use_credentials"
              :label="config.use_credentials.label"
              :helpTooltip="config.use_credentials.help"
            >
              <ItpFormCheckbox
                name="use_credentials"
                v-model="editor.use_credentials"
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="username"
              :label="config.username.label"
              :helpTooltip="config.username.help"
              required
            >
              <ItpFormInput name="username" v-model="editor.username" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="config.password.label"
              :helpTooltip="config.password.help"
            >
              <ItpFormInput
                type="password"
                name="password"
                v-model="change_password.password"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="confirm_password"
              :label="config.confirm_password.label"
              :helpTooltip="config.confirm_password.help"
            >
              <ItpFormInput
                type="password"
                name="confirm_password"
                v-model="change_password.confirm_password"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="tls"
              :label="config.tls.label"
              :helpTooltip="config.tls.help"
            >
              <ItpFormCheckbox name="tls" v-model="editor.tls">
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="ssl"
              :label="config.ssl.label"
              :helpTooltip="config.ssl.help"
            >
              <ItpFormCheckbox name="ssl" v-model="editor.ssl">
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="server"
              :label="config.server.label"
              :helpTooltip="config.server.help"
              required
            >
              <ItpFormInput name="server" v-model="editor.server" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="port"
              :label="config.port.label"
              :helpTooltip="config.port.help"
              required
            >
              <ItpFormInput
                type="number"
                name="port"
                v-model="editor.port"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="mail_from_name"
              :label="config.mail_from_name.label"
              :helpTooltip="config.mail_from_name.help"
              required
            >
              <ItpFormInput
                name="mail_from_name"
                v-model="editor.mail_from_name"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="validate_certs"
              :label="config.validate_certs.label"
              :helpTooltip="config.validate_certs.help"
            >
              <ItpFormCheckbox
                name="validate_certs"
                v-model="editor.validate_certs"
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="timeout"
              :label="config.timeout.label"
              :helpTooltip="config.timeout.help"
              required
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="timeout"
                v-model="editor.timeout"
                required
              >
              </ItpFormInput>
              <span class="input-group-text">
                {{ gettext('sec') }}
              </span>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <ItpForm
        :name="sendTestEmailFormName"
        :labelAlign="$config.forms.defaultLabelAlign"
        v-if="!resource.isNew"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <hr />
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="recipient"
          :label="pgettext('MailAccountConfig', 'Send test email')"
          required
        >
          <div class="input-group">
            <ItpFormInput name="recipient" v-model="recipient" required>
            </ItpFormInput>
            <ItpButton
              type="submit"
              :text="pgettext('MailAccountConfig|Button', 'Send')"
              :spinning="actions.sendTestEmail.isRunning"
              @click.prevent.stop="
                evh_2928017519152566_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </div>
          <ItpAlert
            type="success"
            :show="sendMailTestResult === 'success'"
            dismissible
            class="mt-3"
          >
            <span>
              {{
                pgettext(
                  'MailAccountConfig',
                  'Email has been successfully sent.'
                )
              }}
            </span>
          </ItpAlert>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete.isRunning"
          class="me-auto"
          v-if="!resource.isNew"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
